import {
  ALL_PROPS,
  KeyframeProp,
  LayerKeyframes,
  LayerEasings,
  FRAME_OFFSET,
} from '../../store/useAppStore';

export { FRAME_OFFSET, ALL_PROPS };
export type { KeyframeProp, LayerKeyframes, LayerEasings };

// ── Layout constants ──────────────────────────────────────────────────────────
export const RULER_H = 24;
export const ROW_H   = 22; // collapsed layer row
export const SUB_H   = 15; // per-property sub-row when expanded
export const LABEL_W = 132;

/** Pixel radius within which Shift-drag snaps to the playhead / timeline edges. */
export const SNAP_PX = 15;

// ── Property color + label map ───────────────────────────────────────────────
export const PROP_COLOR: Record<KeyframeProp, string> = {
  opacity: '#fbbf24',
  rotateX: '#f87171',
  rotateY: '#4ade80',
  rotateZ: '#818cf8',
  translateX: '#fb923c',
  translateY: '#e879f9',
};

export const PROP_LABEL: Record<KeyframeProp, string> = {
  opacity: 'O',
  rotateX: 'Rx',
  rotateY: 'Ry',
  rotateZ: 'Rz',
  translateX: 'Tx',
  translateY: 'Ty',
};

// ── Camera track constants ────────────────────────────────────────────────────
export type CamProp = 'x' | 'y' | 'z' | 'fov';
export const CAM_PROPS: CamProp[] = ['x', 'y', 'z', 'fov'];
export const CAM_PROP_COLOR: Record<CamProp, string> = { x: '#60a5fa', y: '#4ade80', z: '#fbbf24', fov: '#f87171' };
export const CAM_PROP_LABEL: Record<CamProp, string> = { x: 'X', y: 'Y', z: 'Z', fov: 'FOV' };

// ── Shared types ──────────────────────────────────────────────────────────────
export type SelectedLayerKf = { layerId: string; prop: KeyframeProp; frame: number };
export type LayerKfClipboardItem = {
  layerId: string;
  prop: KeyframeProp;
  frameOffset: number;
  value: boolean | number;
};

// ── Pure helpers ──────────────────────────────────────────────────────────────
export function kfKey(layerId: string, prop: KeyframeProp, frame: number): string {
  return `${layerId}|${prop}|${frame}`;
}

export function formatTime(frame: number, fps: number): string {
  const totalSec = Math.floor(frame / fps);
  const fr = frame % fps;
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  if (mm > 0) return `${mm}:${String(ss).padStart(2, '0')}.${String(fr).padStart(2, '0')}`;
  return `${String(ss).padStart(2, '0')}.${String(fr).padStart(2, '0')}`;
}

export function getAllFrames(kfs: LayerKeyframes | undefined): number[] {
  if (!kfs) return [];
  const s = new Set<number>();
  for (const p of ALL_PROPS) Object.keys(kfs[p]).forEach((f) => s.add(Number(f)));
  return Array.from(s).sort((a, b) => a - b);
}

/** Returns the display color for a summary diamond (single-prop color, or white for multi). */
export function summaryColor(kfs: LayerKeyframes | undefined, frame: number): string {
  if (!kfs) return '#fff';
  const hit = ALL_PROPS.filter((p) => frame in kfs[p]);
  if (hit.length === 1) return PROP_COLOR[hit[0]];
  return '#ffffff';
}

/**
 * Returns the CSS shape properties for a keyframe diamond.
 *   square  = stepped/hold  (rotate 0° so it sits flat)
 *   diamond = linear        (rotate 45°, the default)
 *   circle  = bezier/ease   (rotate 45° + border-radius 50%)
 *
 * Priority: stepped > bezier > linear
 */
export function kfEasingShape(
  layerEasings: LayerEasings | undefined,
  prop: KeyframeProp | 'summary',
  frame: number,
): { rotate: string; borderRadius: string } {
  const props = prop === 'summary' ? ALL_PROPS : [prop as KeyframeProp];
  let hasStepped = false;
  let hasBezier  = false;
  for (const p of props) {
    const d = layerEasings?.[p]?.[frame];
    if (d?.in === 'stepped' || d?.out === 'stepped') hasStepped = true;
    if (d?.in === 'bezier'  || d?.out === 'bezier')  hasBezier  = true;
  }
  if (hasStepped) return { rotate: '0deg',  borderRadius: '1px'  }; // square
  if (hasBezier)  return { rotate: '45deg', borderRadius: '50%'  }; // circle
  return               { rotate: '45deg', borderRadius: '1.5px' }; // diamond
}
